import { ExpenseData } from '../pages/expense/types';

/**
 * Expense Report Status
 */
export type ExpenseStatus = 'pending' | 'approved' | 'rejected';

/**
 * Expense Report with metadata
 */
export interface ExpenseReport extends ExpenseData {
  _id?: string;
  id?: string;
  status: ExpenseStatus;
  createdAt: Date;
  updatedAt: Date;
  submittedBy?: string;
  reviewedBy?: string;
  reviewNote?: string;
}

// 本地存储 key
const STORAGE_KEY = 'expense_reports';

/**
 * 从本地存储获取报销单列表
 */
function getLocalReports(): ExpenseReport[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

/**
 * 保存报销单列表到本地存储
 */
function saveLocalReports(reports: ExpenseReport[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(reports));
}

/**
 * Save a new expense report (本地存储)
 * @param data - Expense data
 * @param userId - Optional user ID
 * @returns Document ID
 */
export const saveExpenseReport = async (
  data: ExpenseData,
  userId: string = 'guest'
): Promise<string> => {
  try {
    const reports = getLocalReports();
    const id = `report_${Date.now()}`;

    const newReport: ExpenseReport = {
      ...data,
      id,
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date(),
      submittedBy: userId
    };

    reports.unshift(newReport);
    saveLocalReports(reports);

    console.log('Expense report saved with ID:', id);
    return id;
  } catch (error: any) {
    console.error('Error saving expense report:', error);
    throw new Error('保存报销单失败: ' + error.message);
  }
};

/**
 * Get all expense reports
 * @returns Array of expense reports
 */
export const getAllExpenseReports = async (): Promise<ExpenseReport[]> => {
  try {
    return getLocalReports();
  } catch (error: any) {
    console.error('Error fetching expense reports:', error);
    throw new Error('获取报销单列表失败: ' + error.message);
  }
};

/**
 * Get expense reports by status
 * @param status - Expense status
 * @returns Array of expense reports
 */
export const getExpenseReportsByStatus = async (
  status: ExpenseStatus
): Promise<ExpenseReport[]> => {
  try {
    const reports = getLocalReports();
    return reports.filter(r => r.status === status);
  } catch (error: any) {
    console.error('Error fetching expense reports by status:', error);
    throw new Error('获取报销单失败: ' + error.message);
  }
};

/**
 * Get a single expense report by ID
 * @param reportId - Report ID
 * @returns Expense report
 */
export const getExpenseReport = async (reportId: string): Promise<ExpenseReport | null> => {
  try {
    const reports = getLocalReports();
    return reports.find(r => r.id === reportId) || null;
  } catch (error: any) {
    console.error('Error fetching expense report:', error);
    throw new Error('获取报销单详情失败: ' + error.message);
  }
};

/**
 * Update expense report status (approve/reject)
 * @param reportId - Report ID
 * @param status - New status
 * @param reviewNote - Optional review note
 * @param reviewerId - Optional reviewer ID
 */
export const updateExpenseStatus = async (
  reportId: string,
  status: ExpenseStatus,
  reviewNote?: string,
  reviewerId?: string
): Promise<void> => {
  try {
    const reports = getLocalReports();
    const index = reports.findIndex(r => r.id === reportId);

    if (index === -1) {
      throw new Error('报销单不存在');
    }

    reports[index] = {
      ...reports[index],
      status,
      reviewedBy: reviewerId || '',
      reviewNote: reviewNote || '',
      updatedAt: new Date()
    };

    saveLocalReports(reports);
    console.log('Expense report updated:', reportId);
  } catch (error: any) {
    console.error('Error updating expense status:', error);
    throw new Error('更新报销单状态失败: ' + error.message);
  }
};

/**
 * Delete an expense report
 * @param reportId - Report ID
 */
export const deleteExpenseReport = async (reportId: string): Promise<void> => {
  try {
    const reports = getLocalReports();
    const filtered = reports.filter(r => r.id !== reportId);
    saveLocalReports(filtered);
    console.log('Expense report deleted:', reportId);
  } catch (error: any) {
    console.error('Error deleting expense report:', error);
    throw new Error('删除报销单失败: ' + error.message);
  }
};

/**
 * Get expense statistics
 * @returns Statistics object
 */
export const getExpenseStatistics = async () => {
  try {
    const allReports = await getAllExpenseReports();

    const stats = {
      total: allReports.length,
      pending: allReports.filter(r => r.status === 'pending').length,
      approved: allReports.filter(r => r.status === 'approved').length,
      rejected: allReports.filter(r => r.status === 'rejected').length,
      totalAmount: allReports.reduce((sum, r) => sum + r.totalAmount, 0),
      pendingAmount: allReports
        .filter(r => r.status === 'pending')
        .reduce((sum, r) => sum + r.totalAmount, 0),
      approvedAmount: allReports
        .filter(r => r.status === 'approved')
        .reduce((sum, r) => sum + r.totalAmount, 0)
    };

    return stats;
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    throw new Error('获取统计数据失败: ' + error.message);
  }
};
